import { Suspense, useEffect, useState } from "react";
import { Routes, Route, useNavigate, Outlet } from "react-router-dom";
import ProtectedRoute from "./routes/ProtectedRoute";
import { routes } from "./routes";
import NoSubscription from "@components/ui/NoSubscription";
import DefaultLayout from "./layouts/DefaultLayout";
import Logout from "./pages/logout";
import Login from "./pages/login";
import { Navigate } from "react-router-dom";
import SplashLoader from "./components/ui/Loaders/SplashLoader";
import { SocketProvider } from "./context/SocketContext";
import SubscribtionSuspended from "@components/ui/SubscribtionSuspended";
import { useAuthLoading, useUserData } from "./context/OAuth/AuthContext";
import MedicalViewGuard from "./routes/MedicalViewGuard";
import NetworkGuard from "@routes/NetworkGuard";
import CreatePlansGuard from "@routes/CreatePlansGuard";
import AuthRoutes from "@routes/AuthRoutes";
import AuthLayout from "@layouts/AuthLayout";
import ResetPassword from "@pages/ResetPassword/PasswordReset";
import PasswordResetConfirm from "@pages/ResetPassword/PasswordResetConfirm";
import AuthLangRedirect from "@routes/AuthLangRedirect";

const AppRoutes = () => {
  const { subscription, user, setShowCreatePlan } = useUserData();
  const { isAuthLoading } = useAuthLoading();
  const bypassLayoutPaths = ["/first-forms", "/create-my-plans"];

  const ActiveRoute = ({ children, activeCheck }) => {
    if (isAuthLoading) {
      return <SplashLoader />;
    }

    if (!activeCheck) {
      return children;
    }
    if (!subscription && !isAuthLoading) {
      return (
        <DefaultLayout>
          <NoSubscription />
        </DefaultLayout>
      );
    }

    const { status } = subscription;

    if (status === "active" || status === "pending-cancel") {
      return children;
    }

    if (status === "cancelled") {
      return (
        <DefaultLayout>
          <SubscribtionSuspended />
        </DefaultLayout>
      );
    }

    return (
      <DefaultLayout>
        <NoSubscription />
      </DefaultLayout>
    );
  };

  // Component for auth routes that redirects if already logged in
  const AuthRoutesLayout = () => {
    return (
      <AuthRoutes>
        <AuthLayout />
      </AuthRoutes>
    );
  };

  // Separate component for protected routes
  const ProtectedRoutesLayout = () => {
    return (
      <ProtectedRoute>
        <Routes>
          {routes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={
                <MedicalViewGuard>
                  <CreatePlansGuard>
                    <ActiveRoute activeCheck={route.activeCheck}>
                      {bypassLayoutPaths.includes(route.path) ? (
                        <route.component
                          setShowCreatePlan={setShowCreatePlan}
                        />
                      ) : (
                        <DefaultLayout>
                          <route.component />
                        </DefaultLayout>
                      )}
                    </ActiveRoute>
                  </CreatePlansGuard>
                </MedicalViewGuard>
              }
            />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </ProtectedRoute>
    );
  };

return (
  <SocketProvider user={user}>
    <NetworkGuard>
      <Routes>
        {/* Layer 1: Auth Guard Check */}
        <Route element={<AuthRoutes />}>
          {/* Layer 2: Visual Auth Layout */}
          <Route element={<AuthLayout />}>
            <Route
              path="/login"
              element={<AuthLangRedirect basePath="/login" />}
            />
            <Route path="/login/:lang" element={<Login />} />
            <Route
              path="/password-reset"
              element={<AuthLangRedirect basePath="/password-reset" />}
            />
            <Route path="/password-reset/:lang" element={<ResetPassword />} />
            <Route
              path="/reset-password"
              element={<AuthLangRedirect basePath="/reset-password" />}
            />
            <Route
              path="/reset-password/:lang"
              element={<PasswordResetConfirm />}
            />
          </Route>
        </Route>

        {/* Logout & Protected routes remain below... */}
        <Route path="/logout" element={<Logout />} />
        <Route path="/*" element={<ProtectedRoutesLayout />} />
      </Routes>
    </NetworkGuard>
  </SocketProvider>
);
};

export default AppRoutes;
